/**
 * Pure document-number logic for the invoice/quote generator, extracted from
 * `generate-invoice-form.tsx` so the sequence + format rules unit-test directly.
 *
 * A CRM document number is `[reseller_code]-[customer_id_4]-[sequence_3]`.
 * Invoices count up from 001; quotes from 500 — so a single customer's invoices
 * and quotes never collide, and the third segment also tells the two apart on
 * legacy rows that predate the explicit `document_type` column.
 *
 * Lives under the `components/generate-invoice-form/` tree, which the financial
 * wall allowlists as financial-only — but there is no money render here anyway.
 */
export type CrmDocumentType = "invoice" | "quote";

/**
 * Infer a document's type: trust an explicit `document_type`, else read the
 * number pattern (3rd `-` segment ≥ 500 ⇒ quote), else default to invoice. The
 * backward-compatible path for rows written before `document_type` existed.
 */
export function inferDocumentType(
  explicit: string | null | undefined,
  invoiceNumber: string | null | undefined,
): CrmDocumentType {
  if (explicit === "invoice" || explicit === "quote") return explicit;
  if (invoiceNumber) {
    const parts = invoiceNumber.split("-");
    if (parts.length === 3) {
      const lastPart = parseInt(parts[2]!, 10);
      if (!Number.isNaN(lastPart)) return lastPart >= 500 ? "quote" : "invoice";
    }
  }
  return "invoice";
}

/** The seed sequence so the NEXT number is 500 for quotes / 001 for invoices
 * (i.e. the value returned when the customer has no documents of that type). */
export function sequenceSeed(documentType: CrmDocumentType): number {
  return documentType === "quote" ? 499 : 0;
}

/**
 * The highest existing sequence (the 3rd `-` segment) across a customer's
 * document numbers, seeded so an empty set yields the pre-first value. The
 * caller adds 1 to get the next number to mint.
 */
export function highestSequence(
  invoiceNumbers: Array<string | null | undefined>,
  documentType: CrmDocumentType,
): number {
  return invoiceNumbers.reduce<number>((max, num) => {
    if (num) {
      const parts = num.split("-");
      if (parts.length === 3) {
        const seq = parseInt(parts[2]!, 10);
        if (!Number.isNaN(seq)) return seq > max ? seq : max;
      }
    }
    return max;
  }, sequenceSeed(documentType));
}

/** `[reseller_code]-[customer_id_4]-[sequence_3]` with zero-padding. */
export function formatDocumentNumber(
  resellerCode: string,
  customerId: string | number,
  sequence: number,
): string {
  const customerIdFormatted = customerId.toString().padStart(4, "0");
  return `${resellerCode}-${customerIdFormatted}-${sequence.toString().padStart(3, "0")}`;
}

/** Fallback used when live generation throws — `700-<id4>-500` (quote) /
 * `700-<id4>-001` (invoice). */
export function fallbackDocumentNumber(
  customerId: string | number,
  documentType: CrmDocumentType,
): string {
  return formatDocumentNumber("700", customerId, documentType === "quote" ? 500 : 1);
}

/**
 * Next number for a DUPLICATE of `sourceNumber`, derived purely from the source
 * string: keep its `[reseller]-[customer]` segments and bump the sequence by 1.
 * Used as the guaranteed fallback when the customer-scoped generator can't run
 * (the source's customer was deleted / is out of the viewer's scope, so there's
 * no live id to query) — a duplicate must NEVER be left with an empty number.
 *
 * Returns `""` when `sourceNumber` isn't a parseable `A-B-C` number, so the
 * caller can leave the field for manual entry rather than mint a garbage value.
 * The sequence is floored into its type's band (quotes ≥ 500, invoices ≥ 1) so a
 * mis-typed source can't produce an out-of-band number.
 */
export function incrementDocumentNumber(
  sourceNumber: string | null | undefined,
  documentType: CrmDocumentType,
): string {
  if (!sourceNumber) return "";
  const parts = sourceNumber.split("-");
  if (parts.length !== 3) return "";
  const [reseller, customer, seqStr] = parts as [string, string, string];
  const seq = parseInt(seqStr, 10);
  if (Number.isNaN(seq)) return "";
  const floor = documentType === "quote" ? 500 : 1;
  const next = Math.max(seq + 1, floor);
  return formatDocumentNumber(reseller, customer, next);
}
